import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiShield, FiCheckCircle, FiUsers, FiTrendingUp, FiDollarSign, FiClock } = FiIcons;

const SmallBusiness = () => {
  const benefits = [
    'Protection against lawsuits and claims',
    'Coverage for property damage',
    'Professional liability protection',
    'Workers compensation coverage',
    'Cyber liability protection',
    'Business interruption coverage'
  ];

  const industries = [
    'Technology & Software',
    'Healthcare & Medical',
    'Professional Services',
    'Retail & E-commerce',
    'Construction & Contractors',
    'Food & Hospitality',
    'Manufacturing',
    'Real Estate'
  ];

  const californiaKeywords = [
    'Los Angeles',
    'San Francisco',
    'San Diego',
    'Sacramento',
    'San Jose',
    'Fresno',
    'Long Beach',
    'Oakland',
    'Bakersfield',
    'Anaheim',
    'Santa Ana',
    'Riverside',
    'Stockton',
    'Irvine',
    'Chula Vista'
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-900 to-blue-700 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h1 className="text-4xl md:text-5xl font-bold mb-6">
                Small Business Insurance for California Companies
              </h1>
              <p className="text-xl text-blue-100 mb-8">
                Comprehensive business insurance solutions designed specifically for small and medium-sized businesses across California. Get protected with Coterie Insurance through Burbank Insurance Services.
              </p>
              <div className="flex justify-center">
                <a
                  href="https://coterie.com/quote"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-orange-500 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-orange-600 transition-colors shadow-xl text-center"
                >
                  Get My Price
                </a>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="bg-white p-8 rounded-xl shadow-2xl"
            >
              <div className="text-center">
                <SafeIcon icon={FiShield} className="h-16 w-16 text-blue-600 mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Get Protected Today</h3>
                <div className="space-y-4 text-gray-700">
                  <div className="flex items-center space-x-3">
                    <SafeIcon icon={FiClock} className="h-5 w-5 text-green-500" />
                    <span>Quotes in under 5 minutes</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <SafeIcon icon={FiDollarSign} className="h-5 w-5 text-green-500" />
                    <span>Competitive pricing</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <SafeIcon icon={FiUsers} className="h-5 w-5 text-green-500" />
                    <span>Expert local support</span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Your Business Needs Insurance
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Protect your California business from unexpected risks and liabilities that could threaten your operations and financial stability.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white p-6 rounded-xl shadow-lg flex items-center space-x-4"
              >
                <SafeIcon icon={FiCheckCircle} className="h-6 w-6 text-green-500 flex-shrink-0" />
                <span className="text-gray-800 font-medium">{benefit}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Industries Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Industries We Serve
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Specialized insurance solutions for businesses across all major industries in California.
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {industries.map((industry, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-blue-50 p-6 rounded-xl text-center hover:bg-blue-100 transition-colors"
              >
                <SafeIcon icon={FiTrendingUp} className="h-8 w-8 text-blue-600 mx-auto mb-3" />
                <h3 className="font-semibold text-gray-900">{industry}</h3>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-blue-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Protect Your Small Business?
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Don't wait until it's too late. Get comprehensive business insurance coverage through our trusted partnership with Coterie Insurance.
          </p>
          <div className="flex justify-center">
            <a
              href="https://coterie.com/quote"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-orange-500 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-orange-600 transition-colors shadow-xl"
            >
              Get My Price Now
            </a>
          </div>
        </div>
      </section>

      {/* SEO Keywords Section */}
      <section className="py-12 bg-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h3 className="text-lg font-semibold text-gray-700 mb-6 text-center">
            Small Business Insurance Coverage Areas in California
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 text-sm text-gray-600">
            {californiaKeywords.map((city, index) => (
              <div key={index} className="text-center p-2 bg-white rounded-lg shadow-sm">
                Small Business Insurance {city}
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default SmallBusiness;